import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { ImagePickerAsset } from 'expo-image-picker';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Menu } from 'react-native-paper';

import { CameraIcon, DeleteIcon, PhotoIcon } from '@/assets';
import { useDeleteTemporaryMediaItem } from '@/features/api/hooks/shared-media/useDeleteTemporaryMediaItem';
import { AspectRatioWarning } from '@/features/media/components/AspectRatioWarning';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { useMediaPicker } from '@/features/media/hooks/useMediaPicker';
import type {
  AvatarPickerResult,
  ImagePickType
} from '@/features/media/types/picker';
import { isOrganizationImageAspectRatioAcceptable } from '@/features/media/utils/imageAspectRatio';
import { pickImages } from '@/features/media/utils/picker';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { isWeb } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  mediaItem?: ClientMediaItem;
  viewSize?: number;
  // When true, only picks image locally without uploading (e.g. registration flow)
  disableAutoUpload?: boolean;
  /** Read-only mode: hides the edit affordance so the menu cannot be opened. */
  disabled?: boolean;
  onChange: (result: AvatarPickerResult) => void;
};

export const AvatarWithMenu: FC<Props> = ({
  mediaItem,
  viewSize = 176,
  disableAutoUpload = false,
  disabled = false,
  onChange
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.MEDIA, {
    keyPrefix: 'components.avatar-with-menu'
  });

  const { pickImage, isImageUploadPending } = useMediaPicker();
  const { mutate: deleteTemporaryMediaItem } = useDeleteTemporaryMediaItem();

  const [menuVisible, setMenuVisible] = useState(false);
  const [localImageUri, setLocalImageUri] = useState<string>();
  const [temporaryMediaItemId, setTemporaryMediaItemId] = useState<string>();
  const [deleted, setDeleted] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [aspectWarning, setAspectWarning] = useState(false);

  const mediaItemId = mediaItem?.id;

  const handleChange = useCallback(
    (id?: string, isDeleted?: boolean, localImage?: ImagePickerAsset) => {
      const oldLogo =
        isDeleted || isEmptyOrNull(mediaItemId) ? undefined : { mediaItemId };

      const logo = isEmptyOrNull(id) ? oldLogo : { temporaryMediaItemId: id };

      onChange({ logo, temporaryMediaItemId: id, localImage });
    },
    [mediaItemId, onChange]
  );

  const handleDeleteTemporaryMediaItem = useCallback(() => {
    if (isEmptyOrNull(temporaryMediaItemId) || disableAutoUpload) return;

    deleteTemporaryMediaItem(temporaryMediaItemId, {
      onError: (e) =>
        console.error('[AvatarMenu]: temporary image delete failed', e)
    });
  }, [deleteTemporaryMediaItem, disableAutoUpload, temporaryMediaItemId]);

  const handlePickImage = useCallback(
    async (pick: ImagePickType) => {
      setMenuVisible(false);

      if (disableAutoUpload) {
        const images = await pickImages(pick);
        const image = images?.[0];
        if (!image) return;

        setLocalImageUri(image.uri);
        setDeleted(false);
        setTemporaryMediaItemId(undefined);
        setAspectWarning(
          !isOrganizationImageAspectRatioAcceptable(image.width, image.height)
        );
        handleChange(undefined, false, image);
      } else {
        const result = await pickImage(pick);
        // Without a temporary item the upload failed — keep the old avatar.
        if (!result?.temporaryMediaItem) return;

        setLocalImageUri(result.image.uri);
        setDeleted(false);
        setAspectWarning(
          !isOrganizationImageAspectRatioAcceptable(
            result.image.width,
            result.image.height
          )
        );

        handleDeleteTemporaryMediaItem();

        const id = result.temporaryMediaItem.id;
        setTemporaryMediaItemId(id);
        handleChange(id, false, result.image);
      }
    },
    [disableAutoUpload, handleDeleteTemporaryMediaItem, handleChange, pickImage]
  );

  const handleDelete = useCallback(() => {
    setMenuVisible(false);
    setLocalImageUri(undefined);
    setDeleted(true);
    setAspectWarning(false);

    handleDeleteTemporaryMediaItem();
    setTemporaryMediaItemId(undefined);
    handleChange(undefined, true);
  }, [handleDeleteTemporaryMediaItem, handleChange]);

  return (
    <View style={styles.wrapper}>
      <ExtendedMenu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <ProfileAvatar
            isLoading={isImageUploadPending}
            mediaItem={deleted ? undefined : mediaItem}
            localImageUri={localImageUri}
            viewSize={viewSize}
            disabled={disabled}
            onEdit={(hasImage) => {
              setMenuVisible(true);
              setCanDelete(hasImage);
            }}
          />
        }
      >
        <Menu.Item
          leadingIcon={({ color, size }) => (
            <PhotoIcon fill={color} width={size} height={size} />
          )}
          onPress={() => handlePickImage('select')}
          title={t('select')}
        />
        {!isWeb && (
          <Menu.Item
            leadingIcon={({ color, size }) => (
              <CameraIcon fill={color} width={size} height={size} />
            )}
            onPress={() => handlePickImage('take')}
            title={t('take')}
          />
        )}
        {canDelete && (
          <Menu.Item
            leadingIcon={({ size }) => (
              <DeleteIcon
                fill={theme.colors.error}
                width={size}
                height={size}
              />
            )}
            onPress={handleDelete}
            title={t('delete')}
            titleStyle={{ color: theme.colors.error }}
          />
        )}
      </ExtendedMenu>
      {aspectWarning && (
        <AspectRatioWarning
          message={t('warnings.nonSquareAspectRatio')}
          maxWidth={320}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: { alignItems: 'center', gap: 12 }
});
